import React from 'react';
import PropTypes from 'prop-types';

import {Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography} from 'views/components';
import {MarkdownEditor, Translate, translate} from 'views/module';

import PillDropdown from './PillDropdown';

const StageDropdown = (props) => {
  const {selectedStage, setSelectedStage} = props;

  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isReasonDialogOpen, setIsReasonDialogOpen] = React.useState(false);
  const [pendingStage, setPendingStage] = React.useState(null);
  const [statusReason, setStatusReason] = React.useState('');

  const statusRequiringReason = ['INACTIVE', 'CLOSED_LOST'];
  const stages = Object.entries(translate('client.Status'));
  const stageData = translate(`client.Status.${selectedStage}`);
  const pendingStageTitle = pendingStage ? translate(`client.Status.${pendingStage}.Title`) : '';
  const onOverlayChange = props.onOverlayChange;

  React.useEffect(() => {
    onOverlayChange?.(isMenuOpen || isReasonDialogOpen);
  }, [isMenuOpen, isReasonDialogOpen, onOverlayChange]);

  const closeReasonDialog = () => {
    setIsReasonDialogOpen(false);
    setPendingStage(null);
    setStatusReason('');
  };

  const handleOptionSelect = (nextStage) => {
    if (!nextStage || nextStage === selectedStage) {
      return;
    }

    if (statusRequiringReason.includes(nextStage)) {
      setPendingStage(nextStage);
      setIsReasonDialogOpen(true);
      return;
    }

    setSelectedStage(nextStage);
  };

  const handleSaveReason = () => {
    const trimmedReason = statusReason.trim();

    if (!pendingStage || !trimmedReason) {
      return;
    }

    setSelectedStage(pendingStage, {
      blob: trimmedReason,
      editorType: 'MARKDOWN',
    });
    closeReasonDialog();
  };

  return (
    <>
      <PillDropdown
        backgroundColor={stageData.Color || 'transparent'}
        borderColor={stageData.ColorBorder || stageData.Color || 'transparent'}
        label={<Translate expression={`client.Status.${selectedStage}.Title`} />}
        menuLabel={translate('client.ClientDetail.Status.Menu.Subtitles.Stage')}
        onOpenChange={setIsMenuOpen}
        onOptionSelect={handleOptionSelect}
        options={stages}
        selectedValue={selectedStage}
        textColor={stageData.ColorText || '#000000'}
      />
      {
        isReasonDialogOpen &&
        <Dialog onClose={closeReasonDialog} open={isReasonDialogOpen}>
          <DialogTitle onClose={closeReasonDialog}>
            <Translate expression='client.ClientDetail.ChangeStatusDialog.ReasonTitle' />
          </DialogTitle>
          <DialogContent>
            <Box sx={{mt: 1, minWidth: {sm: '28rem'}}}>
              <Typography sx={{mb: 2}}>
                <Translate
                  expression='client.ClientDetail.ChangeStatusDialog.ReasonPrompt'
                  substitute={{status: pendingStageTitle}}
                />
              </Typography>
              <Typography variant='body2' sx={{mb: 1}}>
                {translate('client.ClientDetail.ChangeStatusDialog.Reason')}
              </Typography>
              <Typography color='text.secondary' variant='body2' sx={{mb: 1}}>
                {translate('client.ClientDetail.ChangeStatusDialog.ReasonPlaceholder')}
              </Typography>
              <MarkdownEditor
                autoFocus
                height={220}
                value={statusReason}
                onChange={(value) => setStatusReason(value || '')}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button variant='outlined' onClick={closeReasonDialog}>
              <Translate expression='generic.Cancel' />
            </Button>
            <Button color='primary' disabled={!statusReason.trim()} onClick={handleSaveReason}>
              <Translate expression='generic.Save' />
            </Button>
          </DialogActions>
        </Dialog>
      }
    </>
  );
};

StageDropdown.propTypes = {
  onOverlayChange: PropTypes.func,
  selectedStage: PropTypes.string.isRequired,
  setSelectedStage: PropTypes.func.isRequired,
};

export default StageDropdown;
